// src/pages/calidad/LotesRecepcionCaptura.jsx
// Captura de Recepción de Materiales (F-CAL-09).
// Ajustes de la revisión con Calidad (Fernanda):
//  - El campo "Cantidad" se cambió por "Presentación" (cómo se entrega el producto).
//  - "Factura" ahora es "Folio de factura".
//  - Unidad es lista desplegable: kg, litros o gramos.
//  - La caducidad pasó al apartado de datos generales.
import { useState, useEffect } from 'react';
import api from '../../services/api';

const ESTATUS_OPCIONES = ['LIBERADO', 'RECHAZADO', 'RECHAZADO PARCIAL', 'MUESTRA'];
const UNIDADES = [
  {valor: '', etiqueta: '— Selecciona —'},
  {valor: 'pieza', etiqueta: 'pieza'},
  { valor: 'kg', etiqueta: 'kg' },
  { valor: 'litros', etiqueta: 'litros' },
  { valor: 'gramos', etiqueta: 'gramos' },
];

function formularioVacio() {
  return {
    fecha_recepcion: '', muestra_retencion: '', producto: '', proveedor: '', lote: '', caducidad: '',
    cantidad_recibida: '', cantidad_liberada: '', cantidad_rechazada: '', unidad: '— Selecciona —', presentacion: '',
    olor: '', color: '', sabor: '', apariencia: '', observaciones: '',
    estatus: '', recibido_netsuite: '', oc: '', f_alm: '', f_alm_link: '', factura: '', certificado_link: '',
  };
}

export default function LotesRecepcionCaptura() {
  const [form, setForm] = useState(formularioVacio());
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [presentaciones, setPresentaciones] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    api.get('/api/catalogos/productos').then((res) => setProductos(res.data.data)).catch((err) => console.error(err));
    api.get('/api/catalogos/proveedores').then((res) => setProveedores(res.data.data)).catch((err) => console.error(err));
  }, []);

  // Sugerencias de presentación: primero las que ya se han usado con este producto
  useEffect(() => {
    api.get('/api/calidad/lotes-recepcion/presentaciones', { params: { producto: form.producto || undefined } })
      .then((res) => setPresentaciones(res.data.data.map((p) => p.presentacion)))
      .catch(() => setPresentaciones([]));
  }, [form.producto]);

  function actualizar(campo, valor) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  const aNumero = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const faltantes = [
    !form.fecha_recepcion && 'Fecha de recepción',
    !form.producto.trim() && 'Producto',
    !form.estatus && 'Estatus',
  ].filter(Boolean);
  // Borde rojo en los obligatorios vacíos, solo después de intentar guardar
  const estiloObligatorio = (vacio) => (intentoGuardar && vacio ? { ...styles.input, ...styles.inputFaltante } : styles.input);

  async function handleGuardar() {
    setIntentoGuardar(true);
    if (faltantes.length) {
      setMensaje({ tipo: 'error', texto: `Falta llenar: ${faltantes.join(', ')}.` });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    try {
      setGuardando(true);
      setMensaje(null);
      const payload = {
        ...form,
        cantidad_recibida: aNumero(form.cantidad_recibida),
        cantidad_liberada: aNumero(form.cantidad_liberada),
        cantidad_rechazada: aNumero(form.cantidad_rechazada),
        f_alm_link: form.f_alm === 'SI' ? form.f_alm_link : null,
      };
      const res = await api.post('/api/calidad/lotes-recepcion', payload);
      setMensaje({ tipo: 'ok', texto: `✓ Lote ${res.data.data.lote || res.data.data.producto} capturado (${form.estatus}).` });
      setForm(formularioVacio());
      setIntentoGuardar(false);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar el lote.' });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <h1 style={styles.titulo}>Captura de Recepción de Materiales (F-CAL-09)</h1>
      <p style={styles.subtitulo}>Registra cada lote recibido con su resultado de liberación.</p>

      {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>Datos generales</div>
        <div className="fila3">
          <div style={styles.grupo}>
            <label style={styles.label}>Fecha de recepción *</label>
            <input type="date" value={form.fecha_recepcion} onChange={(e) => actualizar('fecha_recepcion', e.target.value)} style={estiloObligatorio(!form.fecha_recepcion)} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Muestra retención</label>
            <input type="text" value={form.muestra_retencion} onChange={(e) => actualizar('muestra_retencion', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Lote</label>
            <input type="text" value={form.lote} onChange={(e) => actualizar('lote', e.target.value)} style={styles.input} />
          </div>
        </div>
        <div className="fila3">
          <div style={styles.grupo}>
            <label style={styles.label}>Producto *</label>
            <select value={form.producto} onChange={(e) => actualizar('producto', e.target.value)} style={estiloObligatorio(!form.producto.trim())}>
              <option value="">— Selecciona —</option>
              {productos.map((p) => <option key={p.netsuite_id} value={p.nombre}>{p.nombre}</option>)}
            </select>
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Proveedor</label>
            <select value={form.proveedor} onChange={(e) => actualizar('proveedor', e.target.value)} style={styles.input}>
              <option value="">— Selecciona —</option>
              {proveedores.map((p) => <option key={p.netsuite_id} value={p.nombre}>{p.nombre}</option>)}
            </select>
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Caducidad <span style={styles.opcional}>(opcional)</span></label>
            <input type="date" value={form.caducidad} onChange={(e) => actualizar('caducidad', e.target.value)} style={styles.input} />
          </div>
        </div>
      </div>

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>Cantidades</div>
        <div style={styles.grupo}>
          <label style={styles.labelChico}>Presentación <span style={styles.opcional}>(cómo se entrega: tambo, costal 25 kg, caja con 1000 sobres…)</span></label>
          <input type="text" list="lista-presentaciones" value={form.presentacion} onChange={(e) => actualizar('presentacion', e.target.value)} style={styles.input} maxLength={100} />
          <datalist id="lista-presentaciones">
            {[...new Set([...presentaciones, 'Tambo', 'Costal', 'Saco', 'Caja', 'Bolsa', 'Bobina', 'Cubeta', 'Garrafa', 'Tarima', 'Granel'])].map((p) => <option key={p} value={p} />)}
          </datalist>
        </div>
        <div className="fila4">
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Cantidad recibida</label>
            <input type="number" step="0.01" min="0" value={form.cantidad_recibida} onChange={(e) => actualizar('cantidad_recibida', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Cantidad liberada</label>
            <input type="number" step="0.01" min="0" value={form.cantidad_liberada} onChange={(e) => actualizar('cantidad_liberada', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Cantidad rechazada</label>
            <input type="number" step="0.01" min="0" value={form.cantidad_rechazada} onChange={(e) => actualizar('cantidad_rechazada', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Unidad</label>
            <select value={form.unidad} onChange={(e) => actualizar('unidad', e.target.value)} style={styles.input}>
              {UNIDADES.map((u) => <option key={u.valor} value={u.valor}>{u.etiqueta}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>Características sensoriales</div>
        <div className="fila2">
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Olor</label>
            <input type="text" value={form.olor} onChange={(e) => actualizar('olor', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Color</label>
            <input type="text" value={form.color} onChange={(e) => actualizar('color', e.target.value)} style={styles.input} />
          </div>
        </div>
        <div className="fila2">
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Sabor</label>
            <input type="text" value={form.sabor} onChange={(e) => actualizar('sabor', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Apariencia</label>
            <input type="text" value={form.apariencia} onChange={(e) => actualizar('apariencia', e.target.value)} style={styles.input} />
          </div>
        </div>
        <div style={styles.grupo}>
          <label style={styles.label}>Observaciones</label>
          <textarea value={form.observaciones} onChange={(e) => actualizar('observaciones', e.target.value)} style={{ ...styles.input, height: '60px', resize: 'vertical' }} />
        </div>
      </div>

      <div style={styles.tarjeta}>
        <div style={styles.seccionTitulo}>Resultado y trazabilidad</div>
        <div className="fila2">
          <div style={styles.grupo}>
            <label style={styles.label}>Estatus *</label>
            <select value={form.estatus} onChange={(e) => actualizar('estatus', e.target.value)} style={estiloObligatorio(!form.estatus)}>
              <option value="">— Selecciona —</option>
              {ESTATUS_OPCIONES.map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
          <div style={styles.grupo}>
            <label style={styles.label}>Recibido NetSuite</label>
            <select value={form.recibido_netsuite} onChange={(e) => actualizar('recibido_netsuite', e.target.value)} style={styles.input}>
              <option value="">— Selecciona —</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>
        </div>
        <div className="fila3">
          <div style={styles.grupo}>
            <label style={styles.labelChico}>OC</label>
            <input type="text" value={form.oc} onChange={(e) => actualizar('oc', e.target.value)} style={styles.input} />
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>F-Alm</label>
            <select value={form.f_alm} onChange={(e) => actualizar('f_alm', e.target.value)} style={styles.input}>
              <option value="">— Selecciona —</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>
          <div style={styles.grupo}>
            <label style={styles.labelChico}>Folio de factura</label>
            <input type="text" value={form.factura} onChange={(e) => actualizar('factura', e.target.value)} style={styles.input} />
          </div>
        </div>
        {form.f_alm === 'SI' && (
          <div style={styles.grupo}>
            <label style={styles.label}>Link del documento F-Alm</label>
            <input type="text" value={form.f_alm_link} onChange={(e) => actualizar('f_alm_link', e.target.value)} style={styles.input} placeholder="https://..." />
          </div>
        )}
        <div style={styles.grupo}>
          <label style={styles.label}>Link del certificado (SharePoint)</label>
          <input type="text" value={form.certificado_link} onChange={(e) => actualizar('certificado_link', e.target.value)} style={styles.input} placeholder="https://..." />
        </div>
      </div>

      {mensaje && (
          <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>
        )}
      <button onClick={handleGuardar} disabled={guardando} style={styles.botonGuardar}>
        {guardando ? 'Guardando...' : 'Capturar lote'}
      </button>
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '820px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '4px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  mensaje: { fontSize: '13px', marginBottom: '14px', fontWeight: 600 },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' },
  seccionTitulo: { fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#2A1830' },
  grupo: { display: 'flex', flexDirection: 'column', gap: '5px' },
  label: { fontSize: '12px', fontWeight: 600, color: '#6E4F80' },
  labelChico: { fontSize: '11px', fontWeight: 600, color: '#8A7690' },
  input: { padding: '8px 10px', fontSize: '13px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7', width: '100%', boxSizing: 'border-box' },
  inputFaltante: { border: '2px solid #B23A48', background: '#FDF3F1' },
  opcional: { fontWeight: 400, color: '#8A7690', fontSize: '11px' },
  botonGuardar: { width: '100%', padding: '12px', fontSize: '14px', fontWeight: 700, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer' },
};